<script lang="ts" setup>
import { ref, computed, watch, nextTick, defineAsyncComponent } from 'vue';

import { useTrackingStore, formatLabel } from '@dh-io-owpi/shared/src/stores/tracking';
import { WbIcon } from '@dh-io-owpi/shared/src/components/wb';

import { vStencilReady } from '@dh-io-owpi/shared/src/directives/stencil-ready.directive';
import type { Feature } from './lib/api/api';
import type { VehicleStyle } from '@dh-io-owpi/backend-api/src/data-contracts';

// Components
import { WbTabs, WbTabContent, WbTab } from '@dh-io-owpi/shared/src/components/ssr/wb';

import { SecondLevel } from '@dh-io-owpi/shared/src/components/SecondLevel';
import SimpleList from './components/SimpleList/SimpleList.vue';
import type { ExtendedFeatureCategory } from './utils';
const VehicleFeaturesDetails = defineAsyncComponent(
  () => import('./components/VehicleFeaturesDetails/VehicleFeaturesDetails.vue'),
);

const props = defineProps<{
  categories: ExtendedFeatureCategory[];
  modelName?: string;
  selectedStyle?: VehicleStyle; // only used for SPA
}>();

const { track } = useTrackingStore();

const DEFAULT_SELECTED_FEATURE_INDEX = -1;

const selectedFeatureIndex = ref(DEFAULT_SELECTED_FEATURE_INDEX);
const wbTabsReady = ref(false);

const selectedFeatureCategory = ref(props.categories[0]);

const remountKey = ref(0);
// update selectedFeatureCategory when we receive the data from props
watch(
  () => props.categories,
  (d) => {
    // force remount of wb7-tabs component to fix issues when changing the categories
    remountKey.value++;
    selectedFeatureCategory.value = d[0];
  },
);

const featureCategories = computed(() => props.categories);
const selectedFeatureCategoryName = computed(() => selectedFeatureCategory.value?.name || '');
const selectedFeatures = computed(() => selectedFeatureCategory.value?.features || []);
const isSidePanelOpen = computed(() => selectedFeatureIndex.value > -1 && !!selectedFeatures.value);

const selectedFeature = computed(() =>
  selectedFeatures.value && selectedFeatureIndex.value > -1
    ? selectedFeatures.value?.[selectedFeatureIndex.value]
    : null,
);

let lastFocussedElement: HTMLElement;

// focus tile again after closing details sidepanel
watch(isSidePanelOpen, async (value) => {
  await nextTick();
  if (!value) lastFocussedElement.focus();
});

let isSimpleListItemClicked = false;

const itemClick = ({ equipmentId }: Feature, index: number): void => {
  isSimpleListItemClicked = true;
  lastFocussedElement = document.activeElement as HTMLElement;

  selectedFeatureIndex.value = getFeatureIndexById(equipmentId);

  track({
    name: 'show_more_details',
    category: 'feature',
    action: `vehicle_features|${formatLabel(selectedFeatureCategory.value?.name)}|features`,
    label: `card_${index + 1}.${formatLabel(selectedFeature.value?.title)}.${selectedFeature.value?.equipmentId}`,
  });
};

const getFeatureIndexById = (id: string | null): number => {
  if (!id) {
    return DEFAULT_SELECTED_FEATURE_INDEX;
  }
  return selectedFeatureCategory.value
    ? selectedFeatureCategory.value.features.findIndex((feature: Feature) => feature.equipmentId === id)
    : DEFAULT_SELECTED_FEATURE_INDEX;
};

const wbchangeHandler = ($event: CustomEvent): void => {
  if ($event.detail) {
    const categories = props.categories;
    let selectedFeatureCategoryIndex = -1;

    for (const [index, category] of categories.entries()) {
      if (categories[index].name === $event.detail) {
        selectedFeatureCategory.value = category;
        selectedFeatureCategoryIndex = index;
        break;
      }
    }

    if (selectedFeatureCategory.value?.slug) location.hash = selectedFeatureCategory.value?.slug;

    track({
      name: 'switch_tab',
      category: 'feature',
      action: `vehicle_features|features`,
      label: `tab_${selectedFeatureCategoryIndex + 1}.${formatLabel(selectedFeatureCategory.value?.name)}`,
    });
  }
};

function handleSwitchDetail(index: number): void {
  const feature = selectedFeatureCategory.value?.features[index];

  // The following condition prevents a tracking change event from being triggered
  // when the user expands the details of a feature already selected in the simple list component.
  if (isSimpleListItemClicked && index === selectedFeatureIndex.value) {
    isSimpleListItemClicked = false;
    return;
  }
  track({
    category: 'feature',
    action: `vehicle_features|${formatLabel(selectedFeatureCategory.value?.name)}|features`,
    label: `change.card_${index + 1}.${formatLabel(feature?.title)}.${feature?.equipmentId}`,
  });
}

const readyHandler = () => {
  wbTabsReady.value = true;
};

const hasMoreItems = computed(() => (selectedFeatureCategory.value?.features.length ?? 0) > 4);

const isExpanded = ref(false);

// close the expanded state when selected style changes (this should only happen in SPA)
watch(
  () => props.selectedStyle,
  (value) => {
    if (value && isExpanded.value) isExpanded.value = false;
  },
);

function expandComponent(): void {
  isExpanded.value = true;
  track({
    name: 'show_more_details',
    category: 'feature',
    action: `vehicle_features|features`,
    label: `button_load_more.shown_4.total_${selectedFeatureCategory.value?.features.length ?? 0}`,
  });
}
</script>
<template>
  <div class="owpi-vehicle-features" :inert="isSidePanelOpen" :data-has-more="hasMoreItems" :data-expanded="isExpanded">
    <div v-if="featureCategories.length > 0" class="owpi-vehicle-features__grid">
      <WbTabs
        :key="remountKey"
        v-stencil-ready="readyHandler"
        :selected="featureCategories[0].name"
        :ssrAttrs="{ class: '_fb-tabs' }"
        @wbchange="wbchangeHandler"
      >
        <WbTab
          v-for="(featureCategory, i) in featureCategories"
          :key="featureCategory.name"
          :name="featureCategory.name"
          :data-hash="featureCategory.slug"
          :ssrAttrs="{ class: 'wbx-button', role: 'tab', 'aria-hidden': i > 0 }"
        >
          {{ featureCategory.name }}
        </WbTab>

        <WbTabContent
          v-for="(featureCategory, i) in featureCategories"
          :key="featureCategory.name"
          :name="featureCategory.name"
          :ssrAttrs="{ role: 'tabpanel', 'aria-hidden': i > 0 }"
          slot="content"
        >
          <SimpleList :items="featureCategory.features" :modelName @item-click="itemClick" />
        </WbTabContent>
      </WbTabs>
      <SecondLevel v-if="isSidePanelOpen" :title="selectedFeatureCategoryName" @close="selectedFeatureIndex = -1">
        <VehicleFeaturesDetails
          :features="selectedFeatures"
          :selectedFeatureIndex="selectedFeatureIndex"
          @switch-detail="handleSwitchDetail"
        />
      </SecondLevel>
    </div>
  </div>
  <div v-if="hasMoreItems" :aria-hidden="isExpanded" class="owpi-vehicle-features_button">
    <button
      class="wbx-round-button wbx-round-button--medium wbx-round-button--solid-level-1"
      :disabled="isExpanded"
      aria-label="Show more"
      @click="expandComponent"
    >
      <WbIcon name="bds/chevron-down/24" />
    </button>
  </div>
</template>

<style lang="scss">
@use './height';

.owpi-vehicle-features {
  margin-inline: auto;
  // dirty fix to prevent the content shadow from being cut off
  max-width: calc(var(--wb-grid-width) + 10px);
  padding-inline: 5px;

  overflow: hidden;

  &[data-has-more='true'][data-expanded='false'] {
    max-height: var(--vf-calculated-height);
    .owpi-simple-list-item:nth-child(n + 5) {
      display: none;
    }
  }
  &[data-has-more='true'][data-expanded='true'] {
    // just something big enough to make sure the content is not cut off and transition works
    max-height: 1000vmax;
    transition: max-height 3s ease-in-out;
  }

  &__grid {
    width: 100%;
  }

  // fallback while mounting
  ._fb-tabs {
    > [aria-hidden='true'] {
      display: none;
    }
    [role='tab'] {
      margin-block: calc(var(--wb-spacing-xxs) + 4px);

      @at-root [data-brand='amg'] & {
        color: var(--wb-white);
      }
    }
    [role='tabpanel'] {
      padding: var(--wb-spacing-s) 0;
    }
  }
}

.owpi-vehicle-features_button {
  background: linear-gradient(
    0deg,
    var(--wb-color-gradient-start) calc(40px + var(--wb-spacing-s)),
    var(--wb-color-gradient-end) 100%
  );

  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: calc(var(--wb-spacing-s) + 110px);
  transition: opacity 0.3s var(--wb-fade);
  z-index: 1;

  // only the button is clickable
  pointer-events: none;
  > button {
    pointer-events: auto;
  }

  &[aria-hidden='true'] {
    opacity: 0;
  }
}
</style>
